import type { Signature } from '@modules/account/types/signature';
import { asSignatureBlock, hasOwnSignature, putSignature } from '../../utils/signature-block';

/**
 * The signature a compose opens with: the one flagged "Set default for new messages". A signature nobody
 * flagged is never inserted, however many there are: the switch is visible, so leaving it off is a choice.
 *
 * A reply or forward also needs the signature's "Include in replies & forwards" switch to be on.
 */
export function pickDefaultSignature(
  signatures: readonly Signature[] | null | undefined,
  forReplyOrForward = false,
): Signature | null {
  const chosen = (signatures ?? []).find((signature) => signature.isDefault) ?? null;
  return chosen && forReplyOrForward && !chosen.includeInReplies ? null : chosen;
}

/**
 * Puts the signature into `bodyHtml`, once: above the quoted original of a reply or forward, else at the end
 * (see `putSignature`). A body that already has a signature block of its own is returned as it is, so seeding
 * twice (the signature list and the account answer in either order) cannot stack two copies. Only the body's
 * own block counts: the quoted original may contain the same text, and that is no reason to leave a reply
 * without one.
 */
export function insertDefaultSignature(bodyHtml: string, signature: Signature | null): string {
  const block = asSignatureBlock(signature?.content);
  if (!block) {
    return bodyHtml;
  }
  const body = bodyHtml ?? '';
  return hasOwnSignature(body) ? body : putSignature(body, block);
}
